import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Chart, ChartCanvas } from '@react-financial-charts/core';
import { XAxis, YAxis } from '@react-financial-charts/axes';
import { CandlestickSeries, OHLCSeries, LineSeries, BarSeries, BollingerSeries, AreaSeries } from '@react-financial-charts/series';
import { discontinuousTimeScaleProviderBuilder } from '@react-financial-charts/scales';
import { CrossHairCursor, MouseCoordinateX, MouseCoordinateY, CurrentCoordinate } from '@react-financial-charts/coordinates';
import { OHLCTooltip, BollingerBandTooltip, MovingAverageTooltip } from '@react-financial-charts/tooltip';
import { ema, sma, bollingerBand } from '@react-financial-charts/indicators';
import { timeFormat } from 'd3-time-format';
import { format } from 'd3-format';
import Icon from '../../../components/AppIcon';
import TradingModal from '../../../components/TradingModal';
import IndicatorSettingsModal from '../../../components/IndicatorSettingsModal';
import Select from '../../../components/ui/Select';
import tradingApi from '../../../services/tradingApi';
import useLanguage from '../../../hooks/useLanguage';
import useTheme from '../../../hooks/useTheme';
import { useToast } from '../../../components/ui/Toast';
import './FinancialChart.css';

const FinancialChart = ({ selectedSymbol }) => {
  const chartRef = useRef(null);
  const [chartType, setChartType] = useState('candlestick');
  const [chartTimeframe, setChartTimeframe] = useState('1H');
  const [tradingModalOpen, setTradingModalOpen] = useState(false);
  const [tradingType, setTradingType] = useState('buy');
  const [currentSymbol, setCurrentSymbol] = useState(selectedSymbol || { symbol: 'EURUSD', bid: 1.1674, ask: 1.1676 });
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [chartLoaded, setChartLoaded] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [indicatorModalOpen, setIndicatorModalOpen] = useState(false);
  const [selectedIndicators, setSelectedIndicators] = useState(['SMA20']);
  const [indicatorSettings, setIndicatorSettings] = useState({});
  const [quickTradeEnabled, setQuickTradeEnabled] = useState(false);
  const [quickTradeVolume, setQuickTradeVolume] = useState('0.01');
  const isZoomingRef = useRef(false);
  const isPanningRef = useRef(false);
  const isMouseActiveRef = useRef(false);
  const dimensionsSetRef = useRef(false);
  const lastDimensionsRef = useRef({ width: 800, height: 600 });
  
  const { t, isRTL } = useLanguage();
  const { theme } = useTheme();
  const toast = useToast();

  const timeframes = ['1M', '5M', '15M', '30M', '1H', '4H', '1D', '1W'];

  // Available indicators
  const availableIndicators = [
    {
      key: 'SMA20',
      name: 'SMA (20)',
      description: 'Simple Moving Average - 20 period',
      color: '#3b82f6',
      defaultSettings: { period: 20 },
      settings: [
        { key: 'period', label: t('period') || 'Period', default: 20, min: 5, max: 200 }
      ]
    },
    {
      key: 'SMA50',
      name: 'SMA (50)',
      description: 'Simple Moving Average - 50 period',
      color: '#f59e0b',
      defaultSettings: { period: 50 },
      settings: [
        { key: 'period', label: t('period') || 'Period', default: 50, min: 5, max: 200 }
      ]
    },
    {
      key: 'EMA12',
      name: 'EMA (12)',
      description: 'Exponential Moving Average - 12 period',
      color: '#8b5cf6',
      defaultSettings: { period: 12 },
      settings: [
        { key: 'period', label: t('period') || 'Period', default: 12, min: 5, max: 200 }
      ]
    },
    {
      key: 'EMA26',
      name: 'EMA (26)',
      description: 'Exponential Moving Average - 26 period',
      color: '#ec4899',
      defaultSettings: { period: 26 },
      settings: [
        { key: 'period', label: t('period') || 'Period', default: 26, min: 5, max: 200 }
      ]
    },
    {
      key: 'BB20',
      name: 'Bollinger Bands',
      description: 'Bollinger Bands - 20 period, 2 std dev',
      color: 'rgba(139, 92, 246, 0.4)',
      defaultSettings: { period: 20, standardDeviation: 2 },
      settings: [
        { key: 'period', label: t('period') || 'Period', default: 20, min: 5, max: 100 },
        { key: 'standardDeviation', label: t('stdDev') || 'Std Dev', default: 2, min: 1, max: 3, step: 0.1 }
      ]
    }
  ];

  // Handle resize - ALWAYS keep full width, NO flickering
  useEffect(() => {
    let resizeTimer = null;
    
    const updateDimensions = () => {
      // NEVER update during any mouse interaction
      if (isZoomingRef.current || isPanningRef.current || isMouseActiveRef.current) {
        return;
      }
      
      if (chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        const newWidth = rect.width || chartRef.current.offsetWidth;
        const newHeight = rect.height || chartRef.current.offsetHeight;
        
        if (newWidth > 0 && newHeight > 0) {
          // Only update if significantly different
          const widthDiff = Math.abs(lastDimensionsRef.current.width - newWidth);
          const heightDiff = Math.abs(lastDimensionsRef.current.height - newHeight);
          
          if (widthDiff > 10 || heightDiff > 10) {
            lastDimensionsRef.current = { width: newWidth, height: newHeight };
            setDimensions({ width: newWidth, height: newHeight });
            dimensionsSetRef.current = true;
          }
        }
      }
    };

    // Initial sizing with multiple retries for full width
    updateDimensions();
    const initialTimeouts = [100, 300, 600, 1000, 1500].map(delay =>
      setTimeout(updateDimensions, delay)
    );
    
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateDimensions, 300);
    };
    
    const resizeObserver = new ResizeObserver(() => {
      // Debounce resize observer
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateDimensions, 300);
    });
    
    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      initialTimeouts.forEach(t => clearTimeout(t));
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, []);

  // Force update dimensions when chartData is ready (only once)
  useEffect(() => {
    if (chartData && chartRef.current && !dimensionsSetRef.current) {
      const updateOnce = () => {
        if (chartRef.current && !isMouseActiveRef.current) {
          const rect = chartRef.current.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            lastDimensionsRef.current = { width: rect.width, height: rect.height };
            setDimensions({ width: rect.width, height: rect.height });
            dimensionsSetRef.current = true;
          }
        }
      };
      
      updateOnce();
      setTimeout(updateOnce, 150);
      setTimeout(updateOnce, 400);
    }
  }, [chartData]);

  // Mouse wheel scroll handler to prevent dimension updates
  useEffect(() => {
    let wheelTimeout = null;
    
    const handleWheel = () => {
      isZoomingRef.current = true;
      isMouseActiveRef.current = true;
      
      clearTimeout(wheelTimeout);
      wheelTimeout = setTimeout(() => {
        isZoomingRef.current = false;
        isMouseActiveRef.current = false;
      }, 1000);
    };

    const chartElement = chartRef.current;
    if (chartElement) {
      chartElement.addEventListener('wheel', handleWheel, { passive: true });
      
      return () => {
        chartElement.removeEventListener('wheel', handleWheel);
        clearTimeout(wheelTimeout);
      };
    }
  }, []);

  // Update selected symbol
  useEffect(() => {
    if (selectedSymbol) {
      setCurrentSymbol(selectedSymbol);
      loadChartData(selectedSymbol.symbol, chartTimeframe);
    }
  }, [selectedSymbol, chartTimeframe]);

  // Load chart data
  const loadChartData = async (symbol, timeframe) => {
    try {
      setConnectionStatus('connecting');
      setChartLoaded(false);

      const response = await tradingApi.getOHLCData(symbol, timeframe);
      
      if (response?.data?.chart && Array.isArray(response.data.chart)) {
        const ohlc = response.data.chart.map(item => ({
          date: new Date(item.timestamp || item.time),
          open: parseFloat(item.open),
          high: parseFloat(item.high),
          low: parseFloat(item.low),
          close: parseFloat(item.close),
          volume: item.volume || 0,
        }));
        
        setRawData(ohlc);
        setConnectionStatus('connected');
        setChartLoaded(true);
      } else {
        throw new Error('Invalid OHLC data');
      }
    } catch (error) {
      console.warn('Chart data loading failed, using mock data:', error);
      setConnectionStatus('simulated');
      const mockData = generateMockOHLCData();
      setRawData(mockData);
      setChartLoaded(true);
    }
  };

  // Process data with indicators
  const dataWithIndicators = useMemo(() => {
    if (rawData.length === 0) return [];

    let processedData = [...rawData];

    // Calculate indicators
    selectedIndicators.forEach(indicatorKey => {
      const settings = indicatorSettings[indicatorKey];
      
      if (indicatorKey === 'SMA20' || indicatorKey === 'SMA50') {
        const period = settings?.period || (indicatorKey === 'SMA20' ? 20 : 50);
        const smaCalculator = sma()
          .options({ windowSize: period })
          .merge((d, c) => { d[`sma${period}`] = c; })
          .accessor(d => d[`sma${period}`]);
        
        processedData = smaCalculator(processedData);
      }
      
      if (indicatorKey === 'EMA12' || indicatorKey === 'EMA26') {
        const period = settings?.period || (indicatorKey === 'EMA12' ? 12 : 26);
        const emaCalculator = ema()
          .options({ windowSize: period })
          .merge((d, c) => { d[`ema${period}`] = c; })
          .accessor(d => d[`ema${period}`]);
        
        processedData = emaCalculator(processedData);
      }
      
      if (indicatorKey === 'BB20') {
        const period = settings?.period || 20;
        const stdDev = settings?.standardDeviation || 2;
        const bb = bollingerBand()
          .options({
            windowSize: period,
            multiplier: stdDev,
            movingAverageType: 'sma'
          })
          .merge((d, c) => { d.bb = c; })
          .accessor(d => d.bb);
        
        processedData = bb(processedData);
      }
    });

    return processedData;
  }, [rawData, selectedIndicators, indicatorSettings]);

  // Process data with discontinuous scale
  useEffect(() => {
    if (dataWithIndicators.length === 0) return;

    try {
      const scaleProvider = discontinuousTimeScaleProviderBuilder().inputDateAccessor(d => d.date);
      const { data, xScale, xAccessor, displayXAccessor } = scaleProvider(dataWithIndicators);

      setChartData({
        data,
        xScale,
        xAccessor,
        displayXAccessor,
      });
    } catch (error) {
      console.error('Error processing chart data:', error);
    }
  }, [dataWithIndicators]);

  // WebSocket real-time updates
  useEffect(() => {
    if (!currentSymbol?.symbol) return;

    const unsubscribe = tradingApi?.subscribe('ohlc', currentSymbol.symbol, (data) => {
      if (data?.chart && Array.isArray(data.chart)) {
        const formattedData = data.chart.map(item => ({
          date: new Date(item.timestamp || item.time || Date.now()),
          open: parseFloat(item.open),
          high: parseFloat(item.high),
          low: parseFloat(item.low),
          close: parseFloat(item.close),
          volume: item.volume || 0,
        }));
        
        setRawData(prev => {
          const combined = [...prev, ...formattedData];
          const sorted = combined.sort((a, b) => a.date - b.date);
          const unique = sorted.filter((item, index, arr) => 
            index === 0 || item.date.getTime() !== arr[index - 1].date.getTime()
          );
          return unique.slice(-200); // Keep last 200 for performance
        });
      }

      // Single candle update
      if (data?.type === 'ohlc_update') {
        const newCandle = {
          date: new Date(data.timestamp || Date.now()),
          open: parseFloat(data.open),
          high: parseFloat(data.high),
          low: parseFloat(data.low),
          close: parseFloat(data.close),
          volume: data.volume || 0,
        };
        
        setRawData(prev => {
          const updated = [...prev];
          const lastIndex = updated.length - 1;
          
          if (lastIndex >= 0 && Math.abs(updated[lastIndex].date.getTime() - newCandle.date.getTime()) < 60000) {
            updated[lastIndex] = newCandle;
          } else {
            updated.push(newCandle);
          }
          
          return updated.slice(-200);
        });
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentSymbol?.symbol]);

  // Generate mock data
  const generateMockOHLCData = () => {
    const data = [];
    let basePrice = 1.16500;
    const now = Date.now();
    const interval = 3600000; // 1 hour

    for (let i = 0; i < 100; i++) {
      const timestamp = now - (100 - i) * interval;
      const open = basePrice + (Math.random() - 0.5) * 0.001;
      const close = open + (Math.random() - 0.5) * 0.0015;
      const high = Math.max(open, close) + Math.random() * 0.0005;
      const low = Math.min(open, close) - Math.random() * 0.0005;
      
      data.push({
        date: new Date(timestamp),
        open: parseFloat(open.toFixed(5)),
        high: parseFloat(high.toFixed(5)),
        low: parseFloat(low.toFixed(5)),
        close: parseFloat(close.toFixed(5)),
        volume: Math.floor(Math.random() * 1000000),
      });
      
      basePrice = close;
    }
    
    return data;
  };

  const handleTradeClick = (type) => {
    // If quick trade is enabled, place order immediately
    if (quickTradeEnabled) {
      handleQuickTrade(type);
    } else {
      // Otherwise, open modal
      setTradingType(type);
      setTradingModalOpen(true);
    }
  };

  const handleQuickTrade = (type) => {
    try {
      const isBuy = type === 'buy';
      const currentPrice = isBuy ? currentSymbol?.ask : currentSymbol?.bid;
      
      // Create new position object (market order)
      const newPosition = {
        id: Date.now(),
        symbol: currentSymbol?.symbol,
        type: type,
        amount: parseFloat(quickTradeVolume),
        volume: parseFloat(quickTradeVolume),
        openPrice: currentPrice,
        currentPrice: currentPrice,
        closePrice: null,
        profit: 0,
        swap: 0,
        commission: 0,
        stopLoss: null,
        takeProfit: null,
        sl: null,
        tp: null,
        openTime: new Date(),
        placeTime: new Date(),
        orderPrice: currentPrice,
        status: 'open'
      };

      console.log('🚀 DISPATCHING NEW POSITION:', newPosition);

      // Dispatch event to add position to PositionsPanel
      const event = new CustomEvent('newPosition', { 
        detail: newPosition 
      });
      window.dispatchEvent(event);
      
      console.log('✅ EVENT DISPATCHED');

      // Show success toast
      const orderTypeText = isBuy ? t('buy') : t('sell');
      const priceText = `@ ${currentPrice?.toFixed(currentSymbol?.symbol?.includes('JPY') ? 3 : 5)}`;
      const successMessage = `${orderTypeText} ${currentSymbol?.symbol} - ${quickTradeVolume} ${t('lot')} ${priceText}\n${t('positionOpened')}`;
      
      toast.success(successMessage, 5000);
    } catch (error) {
      console.error('❌ Failed to place quick trade:', error);
      toast.error(t('orderFailed'), 4000);
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-500';
      case 'connecting': return 'text-yellow-500';
      case 'simulated': return 'text-blue-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected': return 'Wifi';
      case 'connecting': return 'RotateCw';
      case 'error': return 'WifiOff';
      case 'simulated': return 'Zap';
      default: return 'WifiOff';
    }
  };

  const chartTypes = [
    { id: 'candlestick', icon: 'BarChart3', label: t('candlestickChart') },
    { id: 'bar', icon: 'BarChart2', label: t('barChart') },
    { id: 'line', icon: 'TrendingUp', label: t('lineChart') },
  ];

  const timeframeOptions = timeframes.map(tf => ({
    value: tf,
    label: tf
  }));

  const isDark = theme === 'dark';
  
  // Chart colors based on theme
  const chartColors = {
    background: isDark ? '#0f172a' : '#ffffff',
    grid: isDark ? '#1e293b' : '#e5e7eb',
    text: isDark ? '#94a3b8' : '#475569',
    border: isDark ? '#334155' : '#e2e8f0',
    candlestick: {
      up: {
        fill: '#23827D',
        stroke: '#1a6862'
      },
      down: {
        fill: '#B91C1C',
        stroke: '#991b1b'
      }
    },
    line: isDark ? '#23827D' : '#1a6862',
    crosshair: isDark ? '#64748b' : '#94a3b8',
    tooltip: {
      background: isDark ? '#1e293b' : '#ffffff',
      border: isDark ? '#334155' : '#e2e8f0',
      text: isDark ? '#f8fafc' : '#0f172a'
    }
  };

  // Show loading state
  if (!chartData || !chartData.data || chartData.data.length === 0) {
    return (
      <div className={`h-full bg-card flex flex-col ${isRTL ? 'rtl' : ''}`} style={{ background: chartColors.background }}>
        <div className="flex items-center justify-between p-3 border-b border-border flex-wrap gap-2">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-bold text-foreground">{currentSymbol?.symbol || 'EURUSD'}</h3>
            <div className="flex items-center space-x-1">
              <Icon 
                name={getConnectionStatusIcon()} 
                size={14} 
                className={`${getConnectionStatusColor()} ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`}
              />
              <span className={`text-xs ${getConnectionStatusColor()}`}>
                {connectionStatus === 'simulated' ? 'DEMO' : t(connectionStatus)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="TrendingUp" size={64} className="text-muted-foreground mb-4 mx-auto animate-pulse" />
            <p className="text-muted-foreground text-lg">{t('loadingChart') || 'Loading Chart...'}</p>
            <p className="text-xs text-muted-foreground mt-2">{currentSymbol?.symbol || 'EURUSD'} - {chartTimeframe}</p>
          </div>
        </div>
      </div>
    );
  }

  const { data, xScale, xAccessor, displayXAccessor } = chartData;
  const margin = { left: 0, right: 65, top: 0, bottom: 30 };
  const gridHeight = dimensions.height - 30;
  const priceDisplayFormat = format('.5f');
  const timeDisplayFormat = timeFormat('%d %b %H:%M');

  // Calculate visible range - show last 50 candles with right offset
  const start = Math.max(0, data.length - 60);
  const end = data.length - 1;
  const xExtents = [start, end];

  return (
    <div className={`h-full bg-card flex flex-col ${isRTL ? 'rtl' : ''}`} style={{ background: chartColors.background }}>
      {/* Chart Header */}
      <div className="flex items-center justify-between p-3 border-b border-border flex-wrap gap-2 flex-shrink-0">
        <div className="flex items-center space-x-3 min-w-0">
          <h3 className="text-lg font-bold text-foreground">
            {currentSymbol?.symbol || 'EURUSD'}
          </h3>
          <div className="flex items-center space-x-2 text-sm">
            <span className="font-mono text-foreground font-medium">
              {currentSymbol?.bid?.toFixed(5) || '0.00000'}
            </span>
            <span className={`font-mono font-semibold ${
              (currentSymbol?.change || 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {(currentSymbol?.change || 0) >= 0 ? '+' : ''}
              {((currentSymbol?.changePercent || 0))?.toFixed(2)}%
            </span>
          </div>
          
          <div className="flex items-center space-x-1">
            <Icon 
              name={getConnectionStatusIcon()} 
              size={14} 
              className={`${getConnectionStatusColor()} ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`}
            />
            <span className={`text-xs ${getConnectionStatusColor()}`}>
              {connectionStatus === 'simulated' ? 'DEMO' : t(connectionStatus)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Quick Trade Toggle */}
          <div className="flex flex-col items-center gap-0.5">
            <button
              onClick={() => setQuickTradeEnabled(!quickTradeEnabled)}
              className={`p-1.5 rounded transition-colors ${
                quickTradeEnabled
                  ? 'bg-[#23827D] text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
              }`}
              title={quickTradeEnabled ? t('quickTradeActive') || 'Hızlı İşlem Aktif' : t('quickTradeInactive') || 'Hızlı İşlem Pasif'}
            >
              <Icon name={quickTradeEnabled ? 'Zap' : 'ZapOff'} size={16} />
            </button>
            <span className="text-[9px] font-medium text-muted-foreground whitespace-nowrap">
              {t('quickTrade') || 'Quick'}
            </span>
          </div>

          {/* Indicators Button */}
          <button
            onClick={() => setIndicatorModalOpen(true)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-2 ${
              selectedIndicators.length > 0
                ? 'bg-[#23827D] text-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
            }`}
          >
            <Icon name="Settings" size={14} />
            <span className="hidden sm:inline">{t('indicators')}</span>
            {selectedIndicators.length > 0 && (
              <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs font-bold">
                {selectedIndicators.length}
              </span>
            )}
          </button>

          {/* Chart Type Selector */}
          <div className="flex items-center gap-1 bg-muted p-1 rounded-md">
            {chartTypes.map(type => (
              <button
                key={type.id}
                onClick={() => setChartType(type.id)}
                className={`p-1.5 rounded transition-all duration-200 ${
                  chartType === type.id
                    ? 'bg-[#23827D] text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background'
                }`}
                title={type.label}
              >
                <Icon name={type.icon} size={16} />
              </button>
            ))}
          </div>

          {/* Timeframe Selector - Desktop */}
          <div className="hidden lg:flex items-center gap-1">
            {timeframes.map((tf) => (
              <button
                key={tf}
                onClick={() => setChartTimeframe(tf)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  chartTimeframe === tf
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>

          {/* Timeframe Selector - Mobile */}
          <div className="lg:hidden w-24">
            <Select
              value={chartTimeframe}
              onChange={(e) => setChartTimeframe(e.target.value)}
              options={timeframeOptions}
            />
          </div>

          {/* Trading Buttons - Desktop */}
          <button
            onClick={() => handleTradeClick('sell')}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors hidden sm:flex items-center gap-1"
          >
            <span>{t('sell')}</span>
            <span className="font-mono">{currentSymbol?.bid?.toFixed(5)}</span>
          </button>
          
          {/* Quick Trade Volume Input - Desktop */}
          {quickTradeEnabled && (
            <div className="hidden sm:flex items-center gap-1.5 bg-muted px-2 py-1 rounded border border-border">
              <Icon name="Gauge" size={14} className="text-muted-foreground" />
              <input
                type="number"
                value={quickTradeVolume}
                onChange={(e) => setQuickTradeVolume(e.target.value)}
                step="0.01"
                min="0.01"
                max="100"
                className="w-16 px-1.5 py-0.5 text-xs font-mono bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-[#23827D] text-center"
                placeholder="0.01"
              />
              <span className="text-xs text-muted-foreground">{t('lot') || 'Lot'}</span>
            </div>
          )}
          
          <button
            onClick={() => handleTradeClick('buy')}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition-colors hidden sm:flex items-center gap-1"
          >
            <span>{t('buy')}</span>
            <span className="font-mono">{currentSymbol?.ask?.toFixed(5)}</span>
          </button>
        </div>
      </div>

      {/* Mobile Quick Trade & Trading Controls */}
      <div className="sm:hidden border-b border-border bg-muted/20">
        {/* Quick Trade Volume Input - Mobile (Full Width) */}
        {quickTradeEnabled && (
          <div className="px-3 py-2 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="Gauge" size={16} className="text-muted-foreground flex-shrink-0" />
              
              {/* Minus Button */}
              <button
                onClick={() => {
                  const newValue = Math.max(0.01, parseFloat(quickTradeVolume) - 0.01);
                  setQuickTradeVolume(newValue.toFixed(2));
                }}
                className="w-10 h-10 flex items-center justify-center bg-background border border-border rounded hover:bg-muted active:bg-muted/80 transition-colors"
              >
                <Icon name="Minus" size={18} className="text-foreground" />
              </button>

              {/* Volume Input */}
              <div className="flex-1 bg-background px-3 py-2 rounded border border-border">
                <input
                  type="number"
                  value={quickTradeVolume}
                  onChange={(e) => setQuickTradeVolume(e.target.value)}
                  step="0.01"
                  min="0.01"
                  max="100"
                  className="w-full text-sm font-mono bg-transparent focus:outline-none text-center"
                  placeholder="0.01"
                />
              </div>

              {/* Plus Button */}
              <button
                onClick={() => {
                  const newValue = Math.min(100, parseFloat(quickTradeVolume) + 0.01);
                  setQuickTradeVolume(newValue.toFixed(2));
                }}
                className="w-10 h-10 flex items-center justify-center bg-background border border-border rounded hover:bg-muted active:bg-muted/80 transition-colors"
              >
                <Icon name="Plus" size={18} className="text-foreground" />
              </button>

              <span className="text-sm text-muted-foreground font-medium">{t('lot') || 'Lot'}</span>
            </div>
          </div>
        )}

        {/* Trading Buttons - Mobile */}
        <div className="px-3 py-2 flex gap-2">
          <button
            onClick={() => handleTradeClick('sell')}
            className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded transition-colors flex flex-col items-center justify-center"
          >
            <span className="text-xs opacity-80">{t('sell')}</span>
            <span className="font-mono font-bold">{currentSymbol?.bid?.toFixed(5)}</span>
          </button>
          
          <button
            onClick={() => handleTradeClick('buy')}
            className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded transition-colors flex flex-col items-center justify-center"
          >
            <span className="text-xs opacity-80">{t('buy')}</span>
            <span className="font-mono font-bold">{currentSymbol?.ask?.toFixed(5)}</span>
          </button>
        </div>
      </div>

      {/* Professional Financial Chart */}
      <div className="flex-1 relative min-h-0 w-full overflow-hidden" ref={chartRef}>
        {dimensions.width > 100 && dimensions.height > 100 && (
          <div style={{ 
            width: '100%', 
            height: '100%', 
            position: 'absolute',
            inset: 0
          }}>
            <ChartCanvas
              height={dimensions.height}
              width={dimensions.width}
              ratio={window.devicePixelRatio || 1}
              margin={margin}
              data={data}
              displayXAccessor={displayXAccessor}
              seriesName={currentSymbol?.symbol || 'EURUSD'}
              xScale={xScale}
              xAccessor={xAccessor}
              xExtents={xExtents}
              zoomEvent={true}
              panEvent={true}
              clamp={false}
              mouseMoveEvent={true}
            >
            <Chart
              id={1}
              height={gridHeight}
              yExtents={(d) => [d.high, d.low]}
              padding={{ top: 20, bottom: 20 }}
            >
              <XAxis 
                showGridLines
                gridLinesStroke={chartColors.grid}
                gridLinesStrokeWidth={0.5}
                stroke={chartColors.text}
                tickStroke={chartColors.text}
              />
              
              <YAxis 
                showGridLines
                gridLinesStroke={chartColors.grid}
                gridLinesStrokeWidth={0.5}
                tickFormat={priceDisplayFormat}
                stroke={chartColors.text}
                tickStroke={chartColors.text}
              />

              <MouseCoordinateX 
                displayFormat={timeDisplayFormat}
                fill={chartColors.tooltip.background}
                stroke={chartColors.tooltip.border}
                textFill={chartColors.tooltip.text}
              />
              
              <MouseCoordinateY 
                displayFormat={priceDisplayFormat}
                fill={chartColors.tooltip.background}
                stroke={chartColors.tooltip.border}
                textFill={chartColors.tooltip.text}
              />

              {/* Render series based on chart type */}
              {chartType === 'candlestick' && (
                <CandlestickSeries 
                  fill={(d) => d.close > d.open ? chartColors.candlestick.up.fill : chartColors.candlestick.down.fill}
                  stroke={(d) => d.close > d.open ? chartColors.candlestick.up.stroke : chartColors.candlestick.down.stroke}
                  wickStroke={(d) => d.close > d.open ? chartColors.candlestick.up.stroke : chartColors.candlestick.down.stroke}
                  opacity={1}
                />
              )}

              {chartType === 'bar' && (
                <OHLCSeries 
                  stroke={(d) => d.close > d.open ? chartColors.candlestick.up.stroke : chartColors.candlestick.down.stroke}
                />
              )}

              {chartType === 'line' && (
                <LineSeries 
                  yAccessor={(d) => d.close} 
                  strokeStyle={chartColors.line} 
                  strokeWidth={2} 
                />
              )}

              {/* Render Indicators */}
              {selectedIndicators.includes('SMA20') && (
                <LineSeries
                  yAccessor={(d) => d.sma20}
                  strokeStyle="#3b82f6"
                  strokeWidth={1.5}
                />
              )}

              {selectedIndicators.includes('SMA50') && (
                <LineSeries
                  yAccessor={(d) => d.sma50}
                  strokeStyle="#f59e0b"
                  strokeWidth={1.5}
                />
              )}

              {selectedIndicators.includes('EMA12') && (
                <LineSeries
                  yAccessor={(d) => d.ema12}
                  strokeStyle="#8b5cf6"
                  strokeWidth={1.5}
                />
              )}

              {selectedIndicators.includes('EMA26') && (
                <LineSeries
                  yAccessor={(d) => d.ema26}
                  strokeStyle="#ec4899"
                  strokeWidth={1.5}
                />
              )}

              {selectedIndicators.includes('BB20') && (
                <BollingerSeries
                  yAccessor={(d) => d.bb}
                  strokeStyle={{
                    top: 'rgba(139, 92, 246, 0.8)',
                    middle: 'rgba(139, 92, 246, 0.6)',
                    bottom: 'rgba(139, 92, 246, 0.8)',
                  }}
                  fillStyle="rgba(139, 92, 246, 0.4)"
                />
              )}

              <CurrentCoordinate 
                yAccessor={(d) => d.close} 
                fillStyle={chartColors.line} 
                radius={4}
              />

              <OHLCTooltip 
                origin={[8, 8]} 
                textFill={chartColors.tooltip.text}
                labelFill={chartColors.text}
              />

              {selectedIndicators.includes('BB20') && (
                <BollingerBandTooltip
                  origin={[8, 30]}
                  yAccessor={(d) => d.bb}
                  options={indicatorSettings.BB20 || { windowSize: 20, multiplier: 2 }}
                  textFill={chartColors.tooltip.text}
                  labelFill={chartColors.text}
                />
              )}

              <CrossHairCursor 
                strokeStyle={chartColors.crosshair}
                strokeDasharray="Dash"
              />
            </Chart>
          </ChartCanvas>
          </div>
        )}

        {/* Chart Instructions */}
        <div className="absolute bottom-4 right-4 bg-card/90 backdrop-blur-sm border border-border rounded-lg px-3 py-2 shadow-lg text-xs text-muted-foreground z-10">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Icon name="MousePointer" size={12} />
              <span>Scroll: Zoom | Drag: Pan | Double Click: Reset</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Move" size={12} />
              <span>Shift+Drag: Zoom to selection</span>
            </div>
          </div>
        </div>
      </div>

      {/* Trading Modal */}
      <TradingModal
        isOpen={tradingModalOpen}
        onClose={() => setTradingModalOpen(false)}
        type={tradingType}
        symbol={currentSymbol}
      />

      {/* Indicator Settings Modal */}
      <IndicatorSettingsModal
        isOpen={indicatorModalOpen}
        onClose={() => setIndicatorModalOpen(false)}
        availableIndicators={availableIndicators}
        selectedIndicators={selectedIndicators}
        onSave={(indicators, settings) => {
          setSelectedIndicators(indicators);
          setIndicatorSettings(settings);
        }}
      />
    </div>
  );
};

export default FinancialChart;

